import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Plus,
  Save,
  X,
  Edit,
  Trash2,
  Receipt,
  Percent,
  DollarSign,
  AlertCircle,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_update_taxes,
  get_all_taxes,
} from "../ServiceConnection/serviceconnection";

export default function TaxOptions() {
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [taxes, setTaxes] = useState([]);
  const [errors, setErrors] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    tax_name: "",
    tax_percent_amount: "",
    tax_type: "percentage",
    apply_on: "bill_amount",
  });
  const [editId, setEditId] = useState(0);

  // Dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);

  // Options
  const taxTypeOptions = [
    { value: "percentage", label: "Percentage (%)" },
    { value: "fixed_amount", label: "Fixed Amount" },
  ];

  const applyOnOptions = [
    { value: "bill_amount", label: "On Bill Amount" },
    { value: "taxable_items", label: "On Taxable Items" },
    { value: "all_items", label: "On All Items" },
  ];

  // Fetch taxes
  const fetchTaxes = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 0);

    try {
      const response = await server_post_data(get_all_taxes, fd);

      if (!response.data.error) {
        setTaxes(response.data.message || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching taxes:", error);
      toast({
        title: "Error",
        description: "Failed to load taxes",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.tax_name.trim()) {
      newErrors.tax_name = "Tax name is required";
    }

    if (!formData.tax_percent_amount || formData.tax_percent_amount <= 0) {
      newErrors.tax_percent_amount = "Enter valid value";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("tax_name", formData.tax_name);
    fd.append("tax_percent_amount", formData.tax_percent_amount);
    fd.append("tax_type", formData.tax_type);
    fd.append("apply_on", formData.apply_on);
    fd.append("tax_id", editId);

    try {
      const response = await server_post_data(save_update_taxes, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description:
            editId === 0
              ? "Tax added successfully"
              : "Tax updated successfully",
        });
        resetForm();
        fetchTaxes();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save tax",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [formData, editId, validateForm, toast, fetchTaxes]);

  // Handle edit
  const handleEdit = useCallback((tax) => {
    setFormData({
      tax_name: tax.tax_name,
      tax_percent_amount: tax.tax_percent_amount,
      tax_type: tax.tax_type,
      apply_on: tax.apply_on || "bill_amount",
    });
    setEditId(tax.primary_id);
    setErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!selectedTax) return;

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("tax_id", selectedTax.primary_id);
    fd.append("flag_for", 3);

    try {
      const response = await server_post_data(save_update_taxes, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete tax",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Tax deleted successfully",
        });
        fetchTaxes();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: "Failed to delete tax",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsDeleteDialogOpen(false);
      setSelectedTax(null);
    }
  }, [selectedTax, toast, fetchTaxes]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      tax_name: "",
      tax_percent_amount: "",
      tax_type: "percentage",
      apply_on: "bill_amount",
    });
    setEditId(0);
    setErrors({});
  }, []);

  // Get display label
  const getDisplayLabel = useCallback((value, type) => {
    if (type === "tax_type") {
      return taxTypeOptions.find((opt) => opt.value === value)?.label || value;
    }
    return applyOnOptions.find((opt) => opt.value === value)?.label || value;
  }, []);

  // Initial load
  useEffect(() => {
    fetchTaxes();
  }, [fetchTaxes]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Tax Options</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Manage taxes applied to bills and items
        </p>
      </div>

      {/* Add/Edit Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt className="w-4 h-4 text-primary" />
            {editId === 0 ? "Add New Tax" : "Edit Tax"}
          </CardTitle>
          <CardDescription>
            {editId === 0
              ? "Create a new tax configuration"
              : "Update existing tax configuration"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Tax Name */}
            <div className="space-y-2">
              <Label>
                Tax Name <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="e.g. CGST, Service Tax"
                value={formData.tax_name}
                onChange={(e) => handleFieldChange("tax_name", e.target.value)}
                className={errors.tax_name ? "border-destructive" : ""}
              />
              {errors.tax_name && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.tax_name}
                </p>
              )}
            </div>

            {/* Tax Value */}
            <div className="space-y-2">
              <Label>
                Tax Value <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                placeholder="e.g. 5 or 100"
                value={formData.tax_percent_amount}
                onChange={(e) =>
                  handleFieldChange("tax_percent_amount", e.target.value)
                }
                className={
                  errors.tax_percent_amount ? "border-destructive" : ""
                }
              />
              {errors.tax_percent_amount && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.tax_percent_amount}
                </p>
              )}
            </div>

            {/* Tax Type */}
            <div className="space-y-2">
              <Label>Tax Type</Label>
              <Select
                value={formData.tax_type}
                onValueChange={(value) => handleFieldChange("tax_type", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {taxTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Apply On */}
            <div className="space-y-2">
              <Label>Apply On</Label>
              <Select
                value={formData.apply_on}
                onValueChange={(value) => handleFieldChange("apply_on", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {applyOnOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
            {editId !== 0 && (
              <Button variant="outline" onClick={resetForm} className="gap-2">
                <X className="w-4 h-4" />
                Cancel
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={showLoaderAdmin}
              className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
            >
              {showLoaderAdmin ? (
                "Saving..."
              ) : editId === 0 ? (
                <>
                  <Plus className="w-4 h-4" />
                  Add Tax
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Update Tax
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Taxes Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tax List</CardTitle>
          <CardDescription>
            Manage and configure your tax options
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground w-[80px]">
                    Sr.No
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Tax Name
                  </TableHead>
                  <TableHead className="text-muted-foreground">Value</TableHead>
                  <TableHead className="text-muted-foreground">Type</TableHead>
                  <TableHead className="text-muted-foreground">
                    Apply On
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Status
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taxes.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-muted-foreground"
                    >
                      No taxes found. Add your first tax above.
                    </TableCell>
                  </TableRow>
                ) : (
                  taxes.map((tax, index) => (
                    <TableRow key={tax.primary_id} className="border-border">
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell className="font-medium">
                        {tax.tax_name}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {tax.tax_type === "percentage" ? (
                            <Percent className="w-3 h-3 text-muted-foreground" />
                          ) : (
                            <DollarSign className="w-3 h-3 text-muted-foreground" />
                          )}
                          {tax.tax_percent_amount}
                          {tax.tax_type === "percentage" && "%"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {tax.tax_type === "percentage"
                            ? "Percentage"
                            : "Fixed"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {getDisplayLabel(tax.apply_on, "apply_on")}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={tax.is_active === 1}
                          onCheckedChange={() => {
                            setSelectedTax(tax);
                            setIsToggleDialogOpen(true);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(tax)}
                            className="hover:bg-primary/10 hover:text-primary"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedTax(tax);
                              setIsDeleteDialogOpen(true);
                            }}
                            className="hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tax</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>{selectedTax?.tax_name}</strong>? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Toggle Status Confirmation Dialog */}
      <AlertDialog
        open={isToggleDialogOpen}
        onOpenChange={setIsToggleDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Tax Status</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to{" "}
              {selectedTax?.is_active === 1 ? "deactivate" : "activate"}{" "}
              <strong>{selectedTax?.tax_name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsToggleDialogOpen(false);
                fetchTaxes();
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
